// @flow
import * as React from 'react';

import { getInterpolatedMessageBody } from 'chat/lib/customizedMessagingHelper';
import { setAutoMessageStatus } from 'admin/actions/AutoMessageActions';
import AdminNewAutoMessageReviewIncompleteItems from './AdminNewAutoMessageReviewIncompleteItems';
import AdminNewAutoMessageReviewMessagePreviews from './AdminNewAutoMessageReviewMessagePreviews';
import AdminNewAutoMessageReviewTestMessage from './AdminNewAutoMessageReviewTestMessage';
import Button from 'components/Button';
import Container from 'components/Container';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import View from 'components/View';
import WizardPageLayout from 'components/WizardPageLayout';

import type {
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent as AutoMessageMessageContent,
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_sender as AutoMessageSender,
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_organization as AutoMessageOrg,
} from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';
import type {
  AdminNewAutoMessageOrgs_organizations,
  SaveAutoMessageInput,
  AutoMessageStatus,
} from './queries/__generated__/AdminNewAutoMessageOrgs';
import type { AutoMessageTabKey } from './';
import type { ParsedResults } from 'components/File/CSVParser';

import './styles/AdminNewAutoMessageReview.scss';

export type Props = {
  sender: ?AutoMessageSender,
  messageContent: ?AutoMessageMessageContent,
  organization: AutoMessageOrg,
  messageUuid: string,
  messageName: string,
  formatCSVComplete: boolean,
  syncDataComplete: boolean,
  composeComplete: boolean,
  onSave: (fields: SaveAutoMessageInput, onSuccess: Function) => void,
  onBack: () => void,
  onClose: () => void,
  onJump: (key: AutoMessageTabKey) => void,
  selectedOrg: AdminNewAutoMessageOrgs_organizations,
  isActivatable: boolean,
  status: AutoMessageStatus,
  customMessageFields: ?$ReadOnlyArray<string>,
  hasCustomizedMessaging: boolean,
  isUrgent: boolean,
};

type State = {
  parsedResults?: ParsedResults,
  rowToParse: number,
};

class AdminNewAutoMessageReview extends React.Component<Props, State> {
  state = { rowToParse: 0, parsedResults: undefined };

  componentDidMount() {
    EventTracking.trackKey('auto_messaging.review.view');
  }

  getActionButtons = () => {
    const { status, isActivatable, messageName, messageUuid, organization, onClose } = this.props;
    function SaveButton(props: { label: string }) {
      return (
        <Button
          level="secondary"
          data-test="save-draft-button"
          onClick={() => {
            onClose();
            ToastActions.showSuccessToast(`${messageName} was successfully saved.`);
          }}>
          {props.label}
        </Button>
      );
    }
    function DeactivateButton() {
      return (
        <Button
          level="secondary"
          data-test="deactivate-button"
          onClick={() =>
            setAutoMessageStatus(messageUuid, 'inactive', organization.uuid, messageName, onClose)
          }>
          Deactivate
        </Button>
      );
    }
    function ActivateButton(props: { label: string }) {
      return (
        <Button
          data-test="activate-button"
          disabled={!isActivatable}
          onClick={() =>
            setAutoMessageStatus(messageUuid, 'active', organization.uuid, messageName, onClose)
          }>
          {props.label}
        </Button>
      );
    }

    if (status === 'draft') {
      return [<SaveButton label="Save draft" />, <ActivateButton label="Activate" />];
    }

    if (status === 'inactive') {
      return [<SaveButton label="Keep inactive" />, <ActivateButton label="Activate" />];
    }

    if (status === 'active') {
      return [<DeactivateButton />, <ActivateButton label="Keep active" />];
    }

    console.warn({ status });
    console.error('AdminNewAutoMessageReview - invalid auto message status');
    return [];
  };

  getSenderNameOrSignature = (
    sender: ?AutoMessageSender,
    type: 'name' | 'signature',
    nullDefault: string
  ) => {
    if (!sender) {
      return nullDefault;
    }

    if (sender.__typename === 'UserSender' && type === 'signature') {
      return sender.signature || nullDefault;
    }

    return sender.name || nullDefault;
  };

  getMessageBody = () => {
    const { messageContent, customMessageFields } = this.props;
    const { parsedResults, rowToParse } = this.state;
    const interpolatedMessageBody =
      messageContent && parsedResults && parsedResults.parsedRowData && customMessageFields
        ? getInterpolatedMessageBody(
            customMessageFields,
            messageContent.body,
            parsedResults.parsedRowData[rowToParse]
          )
        : undefined;
    return messageContent && (interpolatedMessageBody || messageContent.body);
  };

  getMessagePreviews = () => {
    const { sender, selectedOrg, isUrgent, messageContent } = this.props;
    return (
      <AdminNewAutoMessageReviewMessagePreviews
        messageBody={this.getMessageBody()}
        fileAttachmentUuids={messageContent && messageContent.fileAttachmentUuids}
        senderNameOrSignature={this.getSenderNameOrSignature(sender, 'signature', 'Sender name')}
        selectedOrgName={selectedOrg && selectedOrg.name}
        isUrgent={isUrgent}
      />
    );
  };

  getTestMessageExperience = () => {
    const { hasCustomizedMessaging, customMessageFields, messageContent } = this.props;
    const { rowToParse, parsedResults } = this.state;
    return (
      <AdminNewAutoMessageReviewTestMessage
        hasCustomizedMessaging={hasCustomizedMessaging}
        rowToParse={rowToParse}
        parsedResults={parsedResults}
        onRowChange={this.handleRowChange}
        onCSVParse={this.handleParse}
        messageBody={this.getMessageBody()}
        fileAttachmentUuids={messageContent && messageContent.fileAttachmentUuids}
        customMessageFields={customMessageFields}
      />
    );
  };

  handleParse = (parsedResults: ParsedResults) => {
    this.setState({ parsedResults }, () => this.setState({ rowToParse: 0 }));
  };

  handleRowChange = (rowToParse: string) => {
    this.setState({ rowToParse: parseInt(rowToParse, 10) });
  };

  render() {
    const {
      sender,
      formatCSVComplete,
      syncDataComplete,
      composeComplete,
      selectedOrg,
      onBack,
      onJump,
    } = this.props;

    return (
      <WizardPageLayout
        actionButtons={this.getActionButtons()}
        backButton={
          <Button data-test="back-button" level="secondary" onClick={onBack}>
            Back
          </Button>
        }>
        <View>
          <Row size="xxxxxlarge">
            <Container flush size="large">
              <Text align="left" lineHeight="large">
                <Row size="small">
                  <Heading level="2">
                    <Text weight="semibold">Test and review auto message</Text>
                  </Heading>
                  <Text>
                    The recipient will receive the following message in a conversation
                    {sender &&
                      ` with ${this.getSenderNameOrSignature(sender, 'name', 'the sender')}`}
                    .
                  </Text>
                </Row>
                <AdminNewAutoMessageReviewIncompleteItems
                  formatCSVComplete={formatCSVComplete}
                  syncDataComplete={syncDataComplete}
                  composeComplete={composeComplete}
                  selectedOrg={selectedOrg}
                  onJump={onJump}
                />
                <Container align="left" size="medium">
                  {this.getMessagePreviews()}
                  {this.getTestMessageExperience()}
                </Container>
              </Text>
            </Container>
          </Row>
        </View>
      </WizardPageLayout>
    );
  }
}

export default AdminNewAutoMessageReview;
